import React from "react";
import { Thermometer, Leaf, FileText, Flame } from "lucide-react";
import BrandschutzCard from "../components/common/cards/BrandschutzCard";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const imageUrl = new URL("../../assets/Marco_Bild.jpeg", import.meta.url).href;

export default function Main() {
  return (
    <>
      <Helmet>
        <title>
          Feuerungskontrolle & Emissionsmessung | Kaminfeger Knapp Buchs
        </title>
        <meta
          name="description"
          content="Amtliche Feuerungskontrollen für Öl-, Gas- und Holzfeuerungen. Fachgerechte Messung, Dokumentation und Beratung für effiziente und sichere Heizungen."
        />
      </Helmet>

      <main className="kn-page">
        {/* Hero + Einleitung */}
        <div className="flex justify-center mt-24">
          <div className="flex flex-col md:flex-row lg:space-x-24 md:space-x-16">
            <img
              src={imageUrl}
              className="w-auto max-w-full h-auto lg:h-[38em] md:h-[32em] sm:h-[26em] h-[16em] p-4 sm:p-6 md:p-8 lg:p-0 rounded-md shadow-md"
              alt="Feuerungskontrolle"
            />

            <div className="kn-intro-copy max-w-2xl mx-auto p-4 sm:p-6 md:p-8 lg:py-0">
              <h1 className="kn-h1">
                Feuerungskontrolle – Verantwortung für Umwelt und Technik
              </h1>

              <p className="kn-lead">
                Als Feuerungskontrolleur prüfe ich Heizungsanlagen – darunter
                Holz-, Öl- und Gasfeuerungen – auf deren Abgaswerte,
                Energieeffizienz und technischen Zustand. Die Kontrollen
                erfolgen mit moderner Messtechnik.
              </p>

              <p className="kn-body">
                Gemessen werden unter anderem Sauerstoffgehalt, CO₂-Emissionen,
                Rauchgastemperatur und Stickoxidwerte. Die Resultate vergleiche
                ich mit den gesetzlich definierten Grenzwerten der
                Luftreinhalteverordnung (LRV).
              </p>

              <p className="kn-body">
                Ziel ist eine saubere, effiziente und gesetzeskonforme
                Heizungsanlage – gut für Umwelt, Geldbeutel und Nachbarschaft.
              </p>

              <p className="kn-body font-semibold text-knapp-ink">
                Feuerungskontrolle, die wirkt – für Effizienz, Luftqualität und
                klare Vorschriften.
              </p>
            </div>
          </div>
        </div>

        {/* Holzfeuerungskontrolle */}
        <section className="max-w-4xl mx-auto px-4 py-28">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Flame className="w-6 h-6 text-knapp-accent" />
              <h2 className="kn-h3">
                Holzfeuerungskontrolle – natürlich, aber nicht harmlos
              </h2>
            </div>

            <p className="kn-body mb-4">
              Auch Holzfeuerungen unterliegen der Luftreinhalteverordnung. Bei
              Anlagen bis 70 kW erfolgt eine Sichtkontrolle der Anlage, Asche
              und des Brennstofflagers. Wichtig: Nur naturbelassenes, gut
              getrocknetes Holz darf verbrannt werden.
            </p>

            <p className="kn-small text-knapp-accent mb-4">
              Abfallverbrennung (z. B. lackiertes oder behandeltes Holz) ist
              gesetzlich verboten und wird bei Verdacht überprüft.
            </p>

            <div className="flex flex-wrap gap-4 pt-2">
              <a
                href="https://www.fairfeuern.ch/index.php?id=9"
                target="_blank"
                rel="noreferrer"
                className="kn-link"
              >
                🔗 Fair feuern Schweiz
              </a>
              <a
                href="https://www.fedlex.admin.ch/eli/cc/1986/208_208_208/de"
                target="_blank"
                rel="noreferrer"
                className="kn-link"
              >
                🔗 Luftreinhalteverordnung (LRV)
              </a>
            </div>
          </div>
        </section>

        {/* Leistungen */}
        <section className="max-w-6xl mx-auto px-4 pb-20 mt-2">
          <h2 className="kn-h2 text-center mb-10">
            Was ich konkret für Sie tue
          </h2>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Abgasmessung & Effizienzkontrolle",
                icon: Thermometer,
                points: [
                  "CO₂, O₂, NOx, Rauchgastemperatur, Kesselwerte",
                  "Bewertung gemäß LRV-Richtlinien",
                ],
              },
              {
                title: "Beratung bei Handlungsbedarf",
                icon: Leaf,
                points: [
                  "Service-Empfehlungen zur Senkung der Werte",
                  "Sanierungsfrist bei gesetzlichen Abweichungen",
                ],
              },
              {
                title: "Dokumentation & Behördenmeldung",
                icon: FileText,
                points: [
                  "Berichte, Fristsetzung, offizielle Meldungen",
                  "Transparenz gegenüber Eigentümer:innen & Behörden",
                ],
              },
            ].map((service, i) => (
              <BrandschutzCard
                delay={i * 0.2}
                key={service.title}
                className={i % 2 === 0 ? "bg-red-50" : "bg-white"}
              >
                <div className="flex items-center gap-3 mb-4">
                  <service.icon className="w-6 h-6 text-knapp-accent" />
                  <h3 className="kn-h3">{service.title}</h3>
                </div>
                <ul className="list-disc list-outside pl-5 space-y-1 kn-body">
                  {service.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </BrandschutzCard>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center py-12">
          <h3 className="kn-h3 mb-4">
            Jetzt Kontrolle vereinbaren und Emissionen reduzieren.
          </h3>
          <Link to="/kontakt">
            <button className="kn-btn-primary">
              Feuerungskontrolle anfragen
            </button>
          </Link>
        </section>
      </main>
    </>
  );
}
